/* Manual circular carousel. Reorder only flex positions, never clone or detach live video players. */
(() => {
  document.querySelectorAll('[data-story-carousel]').forEach(root => {
    const track = root.querySelector('[data-carousel-track]');
    const cards = [...track.querySelectorAll('.story')];
    const controls = root.querySelector('[data-carousel-controls]');
    const previous = root.querySelector('[data-carousel-prev]');
    const next = root.querySelector('[data-carousel-next]');
    const status = root.querySelector('[data-carousel-status]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    if (cards.length < 2) return;
    const wrap = n => (n % cards.length + cards.length) % cards.length;
    let start = cards.length - 1, unit = 0, animation = 0, timer = 0;
    let moving = false, drag = null, touching = false, suppressClick = false;
    const queue = [];
    const measure = () => cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
    // JS owns snapping after native gestures; no-JS keeps the stylesheet's native snap fallback.
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    track.style.overflowAnchor = 'none';
    function arrange(left) {
      cards.forEach((card, i) => { card.style.order = wrap(i - start); });
      track.scrollLeft = left;
    }
    function update() {
      previous.disabled = next.disabled = false;
      const bounds = track.getBoundingClientRect();
      const visible = cards.map((card, i) => ({ box: card.getBoundingClientRect(), number: i + 1 }))
        .filter(({ box }) => Math.min(box.right, bounds.right) - Math.max(box.left, bounds.left) >= box.width * .5)
        .sort((a, b) => a.box.left - b.box.left).map(c => c.number);
      if (visible.length) status.textContent = visible.join(', ') + ' / ' + cards.length;
    }
    function normalize() {
      if (!unit) return;
      const delta = Math.round(track.scrollLeft / unit) - 1;
      if (delta) {
        const left = track.scrollLeft - delta * unit;
        start = wrap(start + delta);
        arrange(left);
      }
      update();
    }
    function cancel() {
      cancelAnimationFrame(animation);
      clearTimeout(timer);
      moving = false;
      queue.length = 0;
    }
    function animate(left, done) {
      clearTimeout(timer);
      moving = true;
      const from = track.scrollLeft, began = performance.now();
      function tick(now) {
        const progress = reduced.matches ? 1 : Math.min(1, (now - began) / 260);
        track.scrollLeft = from + (left - from) * (1 - Math.pow(1 - progress, 3));
        update();
        if (progress < 1) animation = requestAnimationFrame(tick);
        else { moving = false; normalize(); done?.(); }
      }
      animation = requestAnimationFrame(tick);
    }
    function drain() {
      if (moving || !queue.length) return;
      normalize();
      const direction = queue.shift();
      animate(unit * (1 + direction), drain);
    }
    function step(direction) { queue.push(direction); drain(); }
    function settle() {
      if (moving || drag || touching) return;
      normalize();
      if (Math.abs(track.scrollLeft - unit) > 1) animate(unit);
    }
    function select(index) {
      cancel(); start = wrap(index - 1); arrange(unit); update();
    }
    unit = measure();
    arrange(unit);
    previous.addEventListener('click', () => step(-1));
    next.addEventListener('click', () => step(1));
    track.addEventListener('keydown', event => {
      if (event.target !== track) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); step(event.key === 'ArrowRight' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault(); select(event.key === 'Home' ? 0 : cards.length - 1);
      }
    });
    // Keep source/assistive reading order intact; reveal any offscreen player reached with Tab.
    track.addEventListener('focusin', event => {
      const card = event.target.closest('.story');
      if (!card) return;
      const b = card.getBoundingClientRect(), t = track.getBoundingClientRect();
      if (b.left < t.left - 1 || b.right > t.right + 1) select(cards.indexOf(card));
    });
    track.addEventListener('wheel', () => { cancel(); }, { passive: true });
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse') { cancel(); touching = true; return; }
      if (event.button !== 0 || !event.isPrimary || event.target.closest('button,a,input,select,textarea')) return;
      cancel();
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: track.scrollLeft, active: false };
    });
    window.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        drag.active = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
        track.focus({ preventScroll: true });
      }
      event.preventDefault();
      track.scrollLeft = drag.left - dx;
    }, { passive: false });
    function finishDrag(event) {
      if (event.pointerType !== 'mouse') { touching = false; clearTimeout(timer); timer = setTimeout(settle, 160); }
      if (!drag || event.pointerId !== drag.id) return;
      const current = drag; drag = null;
      if (!current.active) return;
      track.classList.remove('is-dragging');
      if (track.hasPointerCapture(current.id)) track.releasePointerCapture(current.id);
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 0);
      settle();
    }
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);
    track.addEventListener('lostpointercapture', finishDrag);
    track.addEventListener('dragstart', event => event.preventDefault());
    track.addEventListener('click', event => {
      if (suppressClick) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, true);
    track.addEventListener('scroll', () => {
      update(); clearTimeout(timer);
      if (!moving && !drag && !touching) timer = setTimeout(settle, 160);
    }, { passive: true });
    function resize() {
      const nextUnit = measure();
      if (Math.abs(nextUnit - unit) < .1) return;
      const index = wrap(start + Math.round(track.scrollLeft / (unit || nextUnit)));
      cancel(); unit = nextUnit; select(index);
    }
    window.addEventListener('resize', resize, { passive: true });
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(resize).observe(track);
    controls.hidden = false;
    update();
  });
})();
